import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Menu } from "antd";
import DrawerMenu from "rc-drawer";
import "@trendmicro/react-sidenav/dist/react-sidenav.css";
import "rc-drawer/assets/index.css";
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/Dashboard/actions.js";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./consoleSideMenu.scss";
import {
  DashboardOutlined,
  DatabaseOutlined,
  CloudDownloadOutlined,
  AppstoreOutlined,
  AppstoreAddOutlined,
  NodeIndexOutlined,
  BarChartOutlined,
  BulbOutlined,
  FormOutlined,
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  CommentOutlined,
  IdcardOutlined,
  FilterOutlined,
  LockOutlined,
  CompassOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { apiRequest } from "util/services";
import AdvisorIcon from "components/Copilot/AdvisorIcon";
import {
  getLockedNavKeys,
  shouldCollapseLockedNav,
} from "util/navProductAccess";
import { isDashboardPipelineIncomplete } from "components/NestedTabs";
const { SubMenu } = Menu;

/** Ant Design Menu `selectedKeys` must match `Menu.Item` `key` (e.g. `ai-services/reports`). */
function sidebarSelectedKeyFromPathname(pathname) {
  const segs = String(pathname || "").split("/").filter(Boolean);
  if (segs[0] !== "console") return segs[1] || segs[0] || "";
  if (segs[1] === "ai-services") {
    if (segs[2] === "auto-reports-settings" || segs[2] === "auto-refresh") {
      return "ai-services/auto-reports-settings";
    }
    if (segs[2] === "auto-reports" || segs[2] === "reports") {
      return "ai-services/auto-reports";
    }
    return "ai-services/auto-reports";
  }
  if (segs[1] === "my-workspace") return "home";
  // Sonic (ACT) lives under ACT menu; AI Voice Capture is customer widget builder in Feedback Capture.
  if (segs[1] === "pivony-sonic" || segs[1] === "advisor-voice") return "advisor";
  if (segs[1] === "advisor" || !segs[1]) return "advisor";
  if (segs[1] === "discover-products") return "discover-products";
  return segs[1] || "";
}

const NAV_PRODUCT_KEYS = [
  "nav-collect",
  "nav-monitor",
  "nav-intelligence",
  "nav-act",
  "nav-deliver",
];

const CONSOLE_SIDE_NAV_OPEN_KEYS = "consoleSideNavOpenKeys";

function readStoredOpenNavKeys() {
  try {
    const raw = localStorage.getItem(CONSOLE_SIDE_NAV_OPEN_KEYS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        parsed.every((key) => NAV_PRODUCT_KEYS.includes(key))
      ) {
        return parsed;
      }
    }
  } catch (_) {}
  return [...NAV_PRODUCT_KEYS];
}

function navSectionKeyFromPathname(pathname) {
  const segs = String(pathname || "").split("/").filter(Boolean);
  if (segs[0] !== "console") return null;
  const page = segs[1];
  // Lifecycle-based IA: Collect → Monitor → Intelligence → Reporting.
  if (page === "source" || page === "sonic-survey" || page === "pivony-sonic" || page === "sonic-prospect") {
    return "nav-collect";
  }
  if (
    page === "survey" ||
    page === "myDashboards" ||
    page === "global_executive"
  ) {
    return "nav-monitor";
  }
  if (
    page === "my-workspace" ||
    page === "journeys" ||
    page === "industryTopics" ||
    page === "customer-365" ||
    page === "behavioral-segmentation" ||
    page === "teach-ai" ||
    !page
  ) {
    return "nav-intelligence";
  }
  // ACT: Pivony Sonic insight-to-action layer (separate from the AI Voice
  // Capture feature that lives under Sonic Survey in Collect).
  if (page === "sonic" || page === "whatsapp") return "nav-act";
  if (page === "ai-services" || page === "report") return "nav-deliver";
  return null;
}

const ConsoleSideMenu = ({
  user,
  openRightClickPopup,
  setOpenRightClickPopup,
  theme,
  collapsed = false,
  onToggleCollapse,
  hasAdvisorAccess = false,
  advisorAccessReady = true,
}) => {
  const { id } = useParams();
  const [isMobileView, setIsMobileView] = useState(false);
  const [isPrivacyMenuOpen, setIsPrivacyMenuOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState([]);
  const [selectedSubMenu, setSelectedSubMenu] = useState("");
  const [openNavKeys, setOpenNavKeys] = useState(readStoredOpenNavKeys);
  // "Yapay zekâya öğret" shows only where sentiment active learning is on for one of the user's dashboards
  const [teachAi, setTeachAi] = useState(null);
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const currentDashboard = DashboardReducer.dashboard?.ID || id;
  const location = useLocation();
  const params = new URLSearchParams(window.location.search);
  const dispatch = useDispatch();

  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    let alive = true;
    apiRequest("active-learning/summary", "GET", null, true).then((res) => {
      if (alive) setTeachAi(res && res.enabled ? res : null);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        CONSOLE_SIDE_NAV_OPEN_KEYS,
        JSON.stringify(openNavKeys)
      );
    } catch (_) {}
  }, [openNavKeys]);

  useEffect(() => {
    if (window.innerWidth < 1100) {
      setIsMobileView(true);
    }
    function handleResize() {
      if (window.innerWidth < 1100) {
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
      }
    }
    const syncFromPath = (pathname) => {
      setSelectedMenuItem(sidebarSelectedKeyFromPathname(pathname));
      const section = navSectionKeyFromPathname(pathname);
      if (section) {
        setOpenNavKeys((prev) =>
          prev.includes(section) ? prev : [...prev, section]
        );
      }
    };
    syncFromPath(location.pathname);
    const unlisten = history.listen((loc) => {
      syncFromPath(loc.pathname);
    });
    window.addEventListener("resize", handleResize);
    return () => {
      unlisten();
      window.removeEventListener("resize", handleResize);
    };
  }, [history, location.pathname]);

  const DashboardKeys = [
    "DashboardData",
    "Dashboard",
    "journey",
    "insights",
    "geolocation",
    "savedSearches",
    "combined-view",
    "topic-gap",
  ];

  const handleClick = (e) => {
    setOpenRightClickPopup(false);
    if (e.key === "DashboardGroup") return;
    else if (e.key === "DashboardData" || e.key === "savedSearches") {
      // reset popup
      dispatch({
        type: actions.UPDATE_Dashboard,
        payload: {
          showNewReviewsLabel: false,
        },
      });
    }
    for (let l = 1; l >= 1; l++) {
      if (params.has(`level${l}Selection`)) {
        params.delete(`level${l}Selection`);
        history.push({ search: params.toString() });
        dispatch({
          type: actions.UPDATE_SEARCH_STATE,
          payload: {
            [`selectedL${l}Topic`]: null,
          },
        });
      } else {
        break;
      }
    }
    [
      "sentiment",
      "intent",
      "rating",
      "top_topics",
      "topics_operator",
      "platform_id",
      "match_date",
      "hotterm",
      "nps",
      "keyword",
      "pivotFilter",
      "pivots_operator",
      "view_type",
      "topics_type",
      "selected_topic",
      "insightTopicSearch",
      "insightTopicClosed",
      "start_index",
      "page_offset",
      "decisionFilter",
      "cursor_next",
      "cursor_curr",
      "cursor_before",
      "savedSearchID",
      "child_others",
      "parent_others",
    ].forEach((item) => params.delete(item));
    setSelectedMenuItem(e.key);
    if (e.key == "home") {
      history.push({ pathname: "/console/my-workspace" });
    } else if (currentDashboard && DashboardKeys.includes(e.key)) {
      history.push({
        pathname: `/console/${e.key}/${currentDashboard}`,
        search: params.toString(),
      });
    } else {
      history.push({
        pathname: `/console/${e.key}`,
      });
    }
  };

  const toggleOpen = () => {
    document
      .querySelector("#root")
      .setAttribute(
        "style",
        !isPrivacyMenuOpen ? "overflow: scroll; width: 100%; height: 100%;" : ""
      );
    setIsPrivacyMenuOpen(!isPrivacyMenuOpen);
  };

  const titleStyle = {
    color: theme === "dark" ? "white" : "black",
    margin: "18px 23px 10px 23px",
    fontFamily: "Poppins",
    fontSize: "14px",
    width: "174px",
    wordBreak: "break-all",
  };

  const DashboardTitleStyle = {
    marginLeft: "7px",
    marginBottom: "0px",
    color: theme === "dark" ? "" : "#4A4D4E",
  };

  const containerStyle = {
    backgroundColor: "var(--popup)",
    paddingLeft: "23px",
    marginBottom: "-15px",
  };

  const freeTextStyle = {
    marginBottom: "0",
    color: "white",
    paddingTop: "18px",
    paddingRight: "18px",
    paddingBottom: "18px",
    fontFamily: "Poppins",
    fontSize: "18px",
  };

  const buttonStyle = {
    background: "var(--color3)",
    borderColor: "var(--color3)",
    minWidth: "150px",
    color: "white",
    borderRadius: "10px",
  };
  const openLinkNewTab = () => {
    window.open(
      `${window.location.origin + "/console/"}${selectedSubMenu}/${
        currentDashboard ? currentDashboard : ""
      }`,
      "_blank"
    );
  };

  const showRightClickPopup = (
    <div
      className="right-click-popup"
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="right-click-popup-item" onClick={openLinkNewTab}>
        Open link in new tab
      </span>
    </div>
  );

  const handleRightClickPopup = (e) => {
    e.preventDefault();

    if (e.target.dataset.menuId || e.target.parentElement.dataset.menuId) {
      const temp_key =
        e.target.dataset.menuId || e.target.parentElement.dataset.menuId;
      const item_key = temp_key.split("-")[temp_key.split("-").length - 1];

      if (DashboardKeys.includes(item_key)) {
        setSelectedSubMenu(item_key);
        setOpenRightClickPopup(true);
      } else {
        setOpenRightClickPopup(false);
      }
    } else {
      setOpenRightClickPopup(false);
    }
  };
  const handleNavOpenChange = (keys) => {
    const next = keys.filter((key) => NAV_PRODUCT_KEYS.includes(key));
    setOpenNavKeys(next);
  };

  const isCollapsedDesktop = collapsed && !isMobileView;

  const renderNavLinkItem = ({
    key,
    id,
    icon,
    href,
    label,
    tooltip,
    children,
    className,
    trailingAction,
    locked = false,
  }) => (
    <Menu.Item
      key={key}
      id={id}
      icon={icon}
      className={className}
      title={isCollapsedDesktop ? tooltip || label : undefined}
    >
      <a
        className="hyper-link"
        href={href}
        onClick={(e) => e.preventDefault()}
      >
        {children || label}
      </a>
      {locked ? (
        <span
          className={`nav-item-lock${trailingAction ? " nav-item-lock--with-action" : ""}`}
          title={t("nav.locked_product")}
          aria-label={t("nav.locked_product")}
        >
          <LockOutlined />
        </span>
      ) : null}
      {trailingAction && !isCollapsedDesktop ? (
        <span
          className="nav-item-trailing-action"
          role="button"
          tabIndex={0}
          title={trailingAction.tooltip}
          aria-label={trailingAction.tooltip}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            trailingAction.onClick();
          }}
        >
          {trailingAction.icon}
        </span>
      ) : null}
    </Menu.Item>
  );

  // Ordered nav items grouped by lifecycle section. Keys/hrefs are unchanged
  // routes; only the grouping + labels follow the new IA.
  const navItems = {
    collect: [
      {
        key: "source",
        id: "createDashboards",
        icon: <AppstoreAddOutlined />,
        href: `${window.location.origin + "/console/source"}`,
        label: t("nav.ia_create_dashboards"),
      },
      {
        key: "sonic-survey",
        id: "feedbackCapture",
        icon: <FormOutlined />,
        href: `${window.location.origin + "/console/sonic-survey"}`,
        label: t("nav.ia_feedback_capture"),
      },
      {
        key: "sonic-prospect",
        id: "sonicProspect",
        icon: <CommentOutlined />,
        href: `${window.location.origin + "/console/sonic-prospect"}`,
        label: t("nav.ia_sonic_prospect"),
      },
    ],
    monitor: [
      {
        key: "survey",
        id: "survey",
        icon: <DatabaseOutlined />,
        href: `${window.location.origin + "/console/survey"}`,
        label: t("nav.ia_data_volumes"),
      },
      {
        key: "myDashboards",
        id: "myDashboards",
        icon: <AppstoreOutlined />,
        href: `${window.location.origin + "/console/myDashboards"}`,
        label: t("nav.ia_global_dashboards"),
      },
      {
        key: "global_executive",
        icon: <DashboardOutlined />,
        href: `${window.location.origin + "/console/global_executive"}`,
        label: t("nav.ia_kpis_alerts"),
      },
    ],
    intelligence: [
      {
        key: "home",
        id: "myAiInsights",
        icon: <BulbOutlined />,
        href: `${window.location.origin + "/console/my-workspace"}`,
        label: t("nav.ia_my_ai_insights"),
      },
      {
        key: "journeys",
        id: "journeys",
        icon: <NodeIndexOutlined />,
        href: `${window.location.origin + "/console/journeys"}`,
        label: t("nav.ia_customer_journey"),
      },
      {
        key: "industryTopics",
        id: "industryTopics",
        icon: <BarChartOutlined />,
        href: `${window.location.origin + "/console/industryTopics"}`,
        label: t("nav.ia_competitor_analysis"),
      },
      {
        key: "customer-365",
        id: "customer365",
        icon: <IdcardOutlined />,
        href: `${window.location.origin + "/console/customer-365"}`,
        label: t("nav.ia_customer_365", "Customer 365"),
      },
      {
        key: "behavioral-segmentation",
        id: "behavioralSegmentation",
        icon: <FilterOutlined />,
        href: `${window.location.origin + "/console/behavioral-segmentation"}`,
        label: t("nav.ia_behavioral_segmentation", "Micro-Segments"),
      },
      ...(teachAi
        ? [
            {
              key: "teach-ai",
              id: "teachAi",
              icon: <RobotOutlined />,
              href: `${window.location.origin + "/console/teach-ai"}`,
              label: t("nav.ia_teach_ai", "Teach the AI"),
              children: (
                <>
                  {t("nav.ia_teach_ai", "Teach the AI")}
                  {teachAi.waiting ? <span className="nav-item-count">{teachAi.waiting}</span> : null}
                </>
              ),
            },
          ]
        : []),
    ],
    act: [
      {
        key: "sonic",
        id: "sonic",
        icon: <ThunderboltOutlined />,
        href: `${window.location.origin + "/console/sonic"}`,
        label: t("nav.ia_sonic_engage"),
      },
    ],
    reporting: [
      {
        key: "ai-services/auto-reports",
        icon: <FileTextOutlined />,
        href: `${window.location.origin + "/console/ai-services/auto-reports"}`,
        label: t("nav.ia_scheduled_reports"),
        trailingAction: {
          icon: <SettingOutlined />,
          tooltip: t("nav.ia_report_settings"),
          onClick: () =>
            history.push("/console/ai-services/auto-reports?view=settings"),
        },
      },
      {
        key: "report",
        id: "report",
        icon: <CloudDownloadOutlined />,
        href: `${window.location.origin + "/console/report"}`,
        label: t("nav.ia_exports"),
      },
    ],
  };

  const lockedNavKeys = useMemo(
    () =>
      getLockedNavKeys(subscriptionStatus, {
        hasAdvisorAccess,
        advisorAccessReady,
      }),
    [subscriptionStatus, hasAdvisorAccess, advisorAccessReady]
  );
  const collapseLocked = shouldCollapseLockedNav(lockedNavKeys);
  const lockedSet = useMemo(() => new Set(lockedNavKeys), [lockedNavKeys]);
  const sectionItems = (items) => {
    const next = items.map((item) => ({
      ...item,
      locked: lockedSet.has(item.key),
    }));
    return collapseLocked ? next.filter((item) => !item.locked) : next;
  };
  const collectItems = sectionItems(navItems.collect);
  const monitorItems = sectionItems(navItems.monitor);
  const intelligenceItems = sectionItems(navItems.intelligence);
  const actItems = sectionItems(navItems.act);
  const reportingItems = sectionItems(navItems.reporting);
  const advisorLocked = !collapseLocked && lockedSet.has("advisor");

  const SideMenu = () => {
    const collapseLabel = collapsed
      ? t("nav.toggle_sidebar_expand")
      : t("nav.toggle_sidebar_collapse");

    return (
      <div className="console-side-menu-shell">
        <div className="console-side-menu-scroll">
          <Menu
            onClick={handleClick}
            selectedKeys={[selectedMenuItem]}
            openKeys={collapsed && !isMobileView ? [] : openNavKeys}
            onOpenChange={handleNavOpenChange}
            inlineCollapsed={collapsed && !isMobileView}
            mode="inline"
            theme={theme === "dark" ? "dark" : "light"}
            contextMenu={true}
            className={`side-menu-item-wrapper ${
              theme === "dark"
                ? ""
                : theme === "light"
                ? "light-side-menu"
                : "brand-side-menu"
            }`}
          >
            {currentDashboard &&
              DashboardKeys.includes(
                document.location.pathname.split("/")[2]
              ) &&
              window.innerWidth < 1100 && (
                <>
                  <h4 style={titleStyle}>
                    {(DashboardReducer?.dashboard?.Name || "").length > 39
                      ? DashboardReducer.dashboard.Name.substring(0, 40) +
                        "..."
                      : DashboardReducer?.dashboard?.Name}
                  </h4>
                  <Menu.ItemGroup className="sub-menu-item" title={false}>
                    <Menu.Item key="DashboardData">
                      <a
                        className="hyper-link"
                        href={`${window.location.origin +
                          "/console/DashboardData"}/${
                          currentDashboard ? currentDashboard : ""
                        }`}
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                      >
                        {t("selected_dashboard_sidebar.1")}
                      </a>
                    </Menu.Item>
                    {/* <Menu.Item key="Dashboard">
                      <a
                        className="hyper-link"
                        href={`${window.location.origin +
                          "/console/Dashboard"}/${
                          currentDashboard ? currentDashboard : ""
                        }`}
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                      >
                        {t("selected_dashboard_sidebar.2")}
                      </a>
                    </Menu.Item> */}
                    {/* <Menu.Item
                      key="industryTopics"
                    >
                      {t("selected_dashboard_sidebar.3")}
                      {selectedSubMenu === 'fixedTopics' && openRightClickPopup && showRightClickPopup}
                    </Menu.Item> */}
                    <Menu.Item key="combined-view">
                      {t("dashboard.combined_view")}
                    </Menu.Item>
                    <Menu.Item key="topic-gap">
                      {t("dashboard.topic_gap_title", {
                        defaultValue: "Keşfedilen konseptler",
                      })}
                    </Menu.Item>
                    {isDashboardPipelineIncomplete(
                      DashboardReducer?.dashboard?.Status
                    ) && (
                    <Menu.Item key="journey">
                      <a
                        className="hyper-link"
                        href={`${window.location.origin +
                          "/console/journey"}/${
                          currentDashboard ? currentDashboard : ""
                        }`}
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                      >
                        {t("selected_dashboard_sidebar.5")}
                      </a>
                    </Menu.Item>
                    )}
                    <Menu.Item key="insights">
                      <a
                        className="hyper-link"
                        href={`${window.location.origin +
                          "/console/insights"}/${
                          currentDashboard ? currentDashboard : ""
                        }`}
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                      >
                        {t("selected_dashboard_sidebar.6")}
                      </a>
                    </Menu.Item>
                    {DashboardReducer.status?.saved_search_available === true && (
                      <Menu.Item key="savedSearches">
                        <a
                          className="hyper-link"
                          href={`${window.location.origin +
                            "/console/savedSearches"}/${
                            currentDashboard ? currentDashboard : ""
                          }`}
                          onClick={(event) => {
                            event.preventDefault();
                          }}
                        >
                          {t("selected_dashboard_sidebar.8")}
                        </a>
                      </Menu.Item>
                    )}
                    {/* {
                      DashboardReducer.dashboard?.GeoEnabled &&
                      <Menu.Item key="geolocation">
                        {t("selected_dashboard_sidebar.7")}
                        {selectedSubMenu === 'geolocation' && openRightClickPopup && showRightClickPopup}
                      </Menu.Item>
                    } */}
                  </Menu.ItemGroup>
                </>
              )}
            {/* ═══ PIVONY ADVISOR (primary standalone link) ══ */}
            {(!collapseLocked || !lockedSet.has("advisor")) &&
              renderNavLinkItem({
              key: "advisor",
              id: "advisor",
              icon: <AdvisorIcon size="xs" />,
              href: `${window.location.origin + "/console/advisor"}`,
              label: t("nav.sonic_advisor", { defaultValue: t("nav.pivony_advisor") }),
              className: "nav-primary-link",
              locked: advisorLocked,
            })}
            {isCollapsedDesktop ? (
              <>
                {collectItems.map((item) => renderNavLinkItem(item))}
                {monitorItems.map((item) => renderNavLinkItem(item))}
                {intelligenceItems.map((item) => renderNavLinkItem(item))}
                {actItems.map((item) => renderNavLinkItem(item))}
                {reportingItems.map((item) => renderNavLinkItem(item))}
                {collapseLocked
                  ? renderNavLinkItem({
                      key: "discover-products",
                      icon: <CompassOutlined />,
                      href: `${window.location.origin + "/console/discover-products"}`,
                      label: t("nav.discover_products"),
                    })
                  : null}
              </>
            ) : (
              <>
                {collectItems.length ? (
                <SubMenu
                  key="nav-collect"
                  className="nav-product-submenu"
                  title={
                    <span className="nav-section-label">
                      {t("nav.section_collect")}
                    </span>
                  }
                >
                  {collectItems.map((item) => renderNavLinkItem(item))}
                </SubMenu>
                ) : null}

                {monitorItems.length ? (
                <SubMenu
                  key="nav-monitor"
                  className="nav-product-submenu"
                  title={
                    <span className="nav-section-label">
                      {t("nav.section_monitor")}
                    </span>
                  }
                >
                  {monitorItems.map((item) => renderNavLinkItem(item))}
                </SubMenu>
                ) : null}

                {intelligenceItems.length ? (
                <SubMenu
                  key="nav-intelligence"
                  className="nav-product-submenu"
                  title={
                    <span className="nav-section-label">
                      {t("nav.section_intelligence")}
                    </span>
                  }
                >
                  {intelligenceItems.map((item) => renderNavLinkItem(item))}
                </SubMenu>
                ) : null}

                {actItems.length ? (
                <SubMenu
                  key="nav-act"
                  className="nav-product-submenu"
                  title={
                    <span className="nav-section-label">
                      {t("nav.section_act")}
                    </span>
                  }
                >
                  {actItems.map((item) => renderNavLinkItem(item))}
                </SubMenu>
                ) : null}

                {reportingItems.length ? (
                <SubMenu
                  key="nav-deliver"
                  className="nav-product-submenu"
                  title={
                    <span className="nav-section-label">
                      {t("nav.section_deliver")}
                    </span>
                  }
                >
                  {reportingItems.map((item) => renderNavLinkItem(item))}
                </SubMenu>
                ) : null}

                {collapseLocked
                  ? renderNavLinkItem({
                      key: "discover-products",
                      icon: <CompassOutlined />,
                      href: `${window.location.origin + "/console/discover-products"}`,
                      label: t("nav.discover_products"),
                    })
                  : null}
              </>
            )}
          </Menu>
        </div>
        {!isMobileView && onToggleCollapse ? (
          <div
            className={`console-side-footer console-side-footer--${theme}${
              collapsed ? " console-side-footer--collapsed" : ""
            }`}
          >
            <button
              type="button"
              className={`console-side-nav-toggle console-side-nav-toggle--${theme}${
                collapsed ? " console-side-nav-toggle--collapsed" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              aria-label={t("nav.toggle_sidebar_aria")}
              aria-expanded={!collapsed}
              title={collapseLabel}
            >
              <span className="console-side-nav-toggle__icon" aria-hidden="true">
                {collapsed ? <RightOutlined /> : <LeftOutlined />}
              </span>
              {!isCollapsedDesktop ? (
                <span className="console-side-nav-toggle__label">{collapseLabel}</span>
              ) : null}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const BootstrappedMenu = () => {
    if (isMobileView) {
      return (
        <DrawerMenu
          getContainer={null}
          open={isPrivacyMenuOpen}
          level={null}
          onClose={toggleOpen}
          onHandleClick={toggleOpen}
          className={
            theme === "dark" ? "" : theme === "light" ? "light" : "brand"
          }
        >
          {SideMenu()}
        </DrawerMenu>
      );
    }
    return SideMenu();
  };
  return BootstrappedMenu();
};

export default ConsoleSideMenu;
